import React, { useMemo, useRef } from "react";
import * as echarts from "echarts";
import { EChartsOption } from "echarts";
import { useChartTheme, useEChart } from "../useEChart";
import { palette, rgba } from "Styles/tokens";

export type LineChartData = {
  title?: string | null;
  itemColor?: string;
  areaColor: string[];
  data?: number[] | string[];
};

type LineChartProps = {
  chartData: LineChartData[];
  title?: string;
  xAxisLabels: string[] | number[];
  height?: string;
  width?: string;
};

const LineChart: React.FC<LineChartProps> = ({
  title,
  chartData,
  height = "400px",
  width = "100%",
  xAxisLabels,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const theme = useChartTheme();

  const option = useMemo<EChartsOption>(
    () => ({
      background: "transparent",
      title: {
        text: title,
        textStyle: {
          color: theme.text,
          fontSize: "0.8rem",
        },
        left: "left",
      },
      legend: {
        data: chartData.map(({ title }) => ({
          name: title ?? undefined,
          icon: "circle",
        })),
        right: "3%",
        align: "left",
        textStyle: {
          color: theme.text,
        },
        itemGap: 20,
      },
      grid: {
        left: 10,
        right: 10,
        top: 40,
        bottom: 0,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: rgba(palette.black, 0.7),
        textStyle: {
          color: palette.white,
        },
        formatter: (params: any) => {
          const xValue = params[0].name;
          let tooltipContent = `${xValue}<br/>`;
          params.forEach((item: any) => {
            tooltipContent += `${item.seriesName}: ${item.value}<br/>`;
          });
          return tooltipContent;
        },
      },
      xAxis: {
        type: "category",
        data: xAxisLabels,
        axisLabel: {
          color: theme.muted,
        },
        axisLine: {
          lineStyle: {
            color: theme.muted,
          },
        },
        boundaryGap: false,
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        max:
          Math.max(
            ...chartData.flatMap(({ data }) => (data || []).map(Number)),
          ) + 2,
        axisLabel: {
          color: theme.muted,
        },
        axisLine: {
          lineStyle: {
            color: theme.muted,
          },
        },
        axisTick: {
          inside: true,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: rgba(palette.midGray, 0.5),
            type: "dashed",
          },
        },
      },
      series: chartData.map(({ title, data, itemColor, areaColor }) => ({
        name: title ?? undefined,
        type: "line",
        smooth: true,
        symbolSize: 0,
        sampling: "average",
        itemStyle: {
          color: itemColor,
        },
        stack: "a",
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: areaColor[0] },
            { offset: 1, color: areaColor[1] },
          ]),
        },
        data,
      })),
    }),
    [chartData, title, xAxisLabels, theme],
  );

  useEChart(chartRef, option);

  return <div ref={chartRef} style={{ width, height, minWidth: 0 }} />;
};

export default LineChart;
